import { useState } from 'react'
import { useSystemConfig, useUpdateConfig } from '@/hooks/useConfig'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { CreditLimitsConfig } from '@/components/admin/CreditLimitsConfig'
import { KbLimitsConfig } from '@/components/admin/KbLimitsConfig'
import { SystemConfig, KB_CONFIG_KEYS } from '@/types'
import { Save, Loader2 } from 'lucide-react'

const GROUP_LABELS: Record<string, string> = {
  registration: 'Registro',
  pricing: 'Precios',
  credits: 'Creditos',
  quota: 'Cuotas',
  maintenance: 'Mantenimiento',
  periodic_search: 'Busqueda Periodica',
  features: 'Caracteristicas',
}

const GROUP_ORDER = [
  'registration',
  'maintenance',
  'pricing',
  'credits',
  'quota',
  'periodic_search',
  'features',
]

function groupConfigs(configs: SystemConfig[]) {
  // Deduplicate by key (keep first occurrence)
  const seen = new Set<string>()
  const unique: SystemConfig[] = []
  for (const c of configs) {
    if (!seen.has(c.key)) {
      seen.add(c.key)
      unique.push(c)
    }
  }

  const groups: Record<string, SystemConfig[]> = {}
  for (const config of unique) {
    const prefix = config.key.split('.')[0]
    if (!groups[prefix]) groups[prefix] = []
    groups[prefix].push(config)
  }

  // Sort groups by GROUP_ORDER, then any remaining alphabetically
  const sorted = GROUP_ORDER.filter((g) => groups[g])
  const rest = Object.keys(groups)
    .filter((g) => !GROUP_ORDER.includes(g))
    .sort()
  return [...sorted, ...rest].map((prefix) => ({
    prefix,
    label: GROUP_LABELS[prefix] || prefix,
    configs: groups[prefix],
  }))
}

function BooleanConfigCard({ config }: { config: SystemConfig }) {
  const updateConfig = useUpdateConfig()
  const [saving, setSaving] = useState(false)

  const handleToggle = async (checked: boolean) => {
    setSaving(true)
    try {
      await updateConfig.mutateAsync({ key: config.key, value: checked })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base font-medium">{config.description}</CardTitle>
            <Badge variant="secondary" className="font-mono text-xs">
              {config.key}
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Switch
              checked={config.value as boolean}
              onCheckedChange={handleToggle}
              disabled={!config.editable || saving}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {config.value ? 'Activado' : 'Desactivado'}
          </span>
          {config.updated_by && (
            <p className="text-xs text-muted-foreground">
              Editado por {config.updated_by}
              {config.updated_at &&
                ` el ${new Date(config.updated_at).toLocaleDateString('es-ES')}`}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function TextConfigCard({ config }: { config: SystemConfig }) {
  const updateConfig = useUpdateConfig()
  const [editedValue, setEditedValue] = useState<string | number | null>(null)
  const [saving, setSaving] = useState(false)

  const currentValue = editedValue !== null ? editedValue : config.value
  const hasChanged = editedValue !== null && editedValue !== config.value

  const handleSave = async () => {
    if (editedValue === null) return
    setSaving(true)
    try {
      await updateConfig.mutateAsync({ key: config.key, value: editedValue })
      setEditedValue(null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="space-y-1">
          <CardTitle className="text-base font-medium">{config.description}</CardTitle>
          <Badge variant="secondary" className="font-mono text-xs">
            {config.key}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center gap-3">
          {config.type === 'number' ? (
            <Input
              type="number"
              className="max-w-xs"
              value={currentValue as number}
              onChange={(e) => setEditedValue(Number(e.target.value))}
              disabled={!config.editable || saving}
            />
          ) : (
            <Input
              className="max-w-lg"
              value={String(currentValue)}
              onChange={(e) => setEditedValue(e.target.value)}
              disabled={!config.editable || saving}
            />
          )}
          {config.editable && hasChanged && (
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-1 h-4 w-4" />
              )}
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          )}
        </div>
        {config.updated_by && (
          <p className="mt-2 text-xs text-muted-foreground">
            Editado por {config.updated_by}
            {config.updated_at && ` el ${new Date(config.updated_at).toLocaleDateString('es-ES')}`}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export const SettingsPage = () => {
  const { data: configs, isLoading } = useSystemConfig()

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Configuracion</h1>
        <div className="space-y-8">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // kb.* keys are rendered by the dedicated KbLimitsConfig card below (with
  // ceiling helper text + client-side validation) — excluded here to avoid
  // showing them twice (change: excel-knowledge-bases).
  const groups = groupConfigs((configs || []).filter((c) => !KB_CONFIG_KEYS.includes(c.key)))

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Configuracion</h1>

      <CreditLimitsConfig />
      <KbLimitsConfig />

      <div className="space-y-8">
        {groups.map((group) => (
          <section key={group.prefix}>
            <h2 className="mb-3 text-lg font-semibold text-foreground">{group.label}</h2>
            <div className="grid gap-3">
              {group.configs.map((config) =>
                config.type === 'boolean' ? (
                  <BooleanConfigCard key={config.key} config={config} />
                ) : (
                  <TextConfigCard key={config.key} config={config} />
                )
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}
